import React, { memo } from 'react';
import { Badge } from '@/components/ui/badge';
import {
  Check,
  AlertTriangle,
  AlertCircle,
  Clock,
  X,
  Shield
} from 'lucide-react';
import { EVIDENCE_QUALITY, calculateEvidenceQuality } from '@/config/evidenceRules';

/**
 * Evidence Quality Badge
 * Displays the current evidence quality status for a job
 */
function EvidenceQualityBadge({ job, size = 'default', showIcon = true, className = '' }) {
  const quality = calculateEvidenceQuality(job);
  
  const icons = {
    [EVIDENCE_QUALITY.COMPLETE.label]: Check,
    [EVIDENCE_QUALITY.PARTIAL.label]: AlertTriangle,
    [EVIDENCE_QUALITY.MISSING_REQUIRED.label]: AlertCircle,
    [EVIDENCE_QUALITY.AWAITING_REVIEW.label]: Clock,
    [EVIDENCE_QUALITY.REJECTED.label]: X,
    [EVIDENCE_QUALITY.APPROVED.label]: Shield
  };
  
  const Icon = icons[quality.label] || AlertCircle;
  
  const sizeClasses = {
    xs: 'text-[9px] px-1.5 py-0 h-4',
    sm: 'text-[10px] px-2 py-0.5 h-5',
    default: 'text-xs px-2.5 py-1 h-6'
  };
  
  const iconSizes = {
    xs: 'h-2.5 w-2.5',
    sm: 'h-3 w-3',
    default: 'h-3.5 w-3.5'
  };
  
  return (
    <Badge 
      className={`${quality.color} text-white ${sizeClasses[size]} ${className}`}
      data-testid="evidence-quality-badge"
    >
      {showIcon && <Icon className={`${iconSizes[size]} mr-1`} />}
      {quality.label}
    </Badge>
  );
}

/**
 * Evidence Quality Indicator - Inline version
 */
export function EvidenceQualityIndicator({ job, className = '' }) {
  const quality = calculateEvidenceQuality(job);
  
  return (
    <div 
      className={`flex items-center gap-1.5 ${className}`}
      data-testid="evidence-quality-indicator"
    >
      <div className={`w-2 h-2 rounded-full ${quality.color}`} />
      <span className={`text-xs font-medium ${quality.textColor}`}>
        {quality.label}
      </span>
    </div>
  );
}

/**
 * Evidence Quality Card - Detailed version
 */
export function EvidenceQualityCard({ job, className = '' }) {
  const quality = calculateEvidenceQuality(job);
  const evidence = job?.evidence_files || [];
  const review = job?.evidence_review || {};
  
  // Count by category
  const categoryCounts = {};
  evidence.forEach(e => {
    const cat = e.category || 'other';
    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
  });
  
  return (
    <div 
      className={`p-3 rounded-xl border ${quality.bgLight} ${className}`}
      data-testid="evidence-quality-card"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-slate-700">Evidence Status</span>
        <EvidenceQualityBadge job={job} size="sm" />
      </div>
      
      {/* Category Summary */}
      <div className="grid grid-cols-2 gap-1.5 text-[10px]">
        {Object.entries(categoryCounts).map(([cat, count]) => (
          <div key={cat} className="flex items-center justify-between px-2 py-1 bg-white/50 rounded">
            <span className="text-slate-600 capitalize">{cat.replace('_', ' ')}</span>
            <span className="font-medium text-slate-800">{count}</span>
          </div>
        ))}
      </div>
      
      {/* Review Info */}
      {review.status && (
        <div className="mt-2 pt-2 border-t border-slate-200/50">
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-slate-500">Review Status</span>
            <span className={`font-medium capitalize ${
              review.status === 'approved' ? 'text-emerald-600' :
              review.status === 'rejected' ? 'text-red-600' :
              'text-blue-600'
            }`}>
              {review.status}
            </span>
          </div>
          {review.reviewed_by && (
            <div className="text-[9px] text-slate-400 mt-0.5">
              by {review.reviewed_by} on {new Date(review.reviewed_at).toLocaleDateString()}
            </div>
          )}
          {review.notes && (
            <div className="text-[10px] text-slate-600 mt-1 p-1.5 bg-white/50 rounded">
              {review.notes}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default memo(EvidenceQualityBadge);
